import { createClient } from '@supabase/supabase-js';
import type { Database } from './database.types';

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

if (!supabaseUrl || !supabaseAnonKey) {
  throw new Error('Missing Supabase environment variables');
}

// Add retry logic and circuit breaker
const MAX_RETRIES = 3;
const RETRY_DELAY = 1000;
const CIRCUIT_BREAKER_THRESHOLD = 5;
const CIRCUIT_BREAKER_RESET_TIMEOUT = 30000;

let failureCount = 0;
let circuitBreakerOpen = false;
let lastFailureTime = 0;

export const supabase = createClient<Database>(supabaseUrl, supabaseAnonKey, {
  auth: {
    persistSession: true,
    autoRefreshToken: true,
    detectSessionInUrl: true,
    storageKey: 'sb-auth-token',
    storage: sessionStorage
  },
  global: {
    headers: {
      'x-application-name': 'java-dev-week',
      'x-client-info': 'supabase-js/2.39.7',
      'Access-Control-Allow-Origin': 'https://jornadajava.com.br'
    }
  },
  db: {
    schema: 'public'
  }
});

// Enhanced retry logic with circuit breaker
export async function withRetry<T>(
  operation: () => Promise<T>,
  maxRetries = MAX_RETRIES,
  delay = RETRY_DELAY
): Promise<T> {
  // Check circuit breaker
  if (circuitBreakerOpen) {
    const now = Date.now();
    if (now - lastFailureTime >= CIRCUIT_BREAKER_RESET_TIMEOUT) {
      circuitBreakerOpen = false;
      failureCount = 0;
    } else {
      throw new Error('Circuit breaker is open');
    }
  }

  let lastError;
  
  for (let i = 0; i < maxRetries; i++) {
    try {
      const result = await operation();
      failureCount = 0; // Reset failure count on success
      return result;
    } catch (error) {
      lastError = error;
      failureCount++;

      // Check if circuit breaker should open
      if (failureCount >= CIRCUIT_BREAKER_THRESHOLD) {
        circuitBreakerOpen = true;
        lastFailureTime = Date.now();
        throw new Error('Circuit breaker triggered');
      }

      if (i < maxRetries - 1) {
        await new Promise(resolve => setTimeout(resolve, delay * Math.pow(2, i)));
      }
    }
  }
  
  throw lastError;
}

// Add request sanitization
export function sanitizeInput(input: string): string {
  return input
    .replace(/[<>]/g, '') // Remove potential HTML tags
    .trim();
}

// Add data validation
export function validateEmail(email: string): boolean {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
}

// Add security headers
export const securityHeaders = {
  'Content-Security-Policy': "default-src 'self'; img-src 'self' https:; script-src 'self' 'unsafe-inline'; style-src 'self' 'unsafe-inline';",
  'X-Frame-Options': 'DENY',
  'X-Content-Type-Options': 'nosniff',
  'Referrer-Policy': 'strict-origin-when-cross-origin',
  'Permissions-Policy': 'camera=(), microphone=(), geolocation=()'
};